import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartPie,
  faPenToSquare,
  faGear,
  faFileUpload,
  faGlobe,
  faBook,
  faLanguage,
  faChartLine,
  faSpellCheck,
  faChevronDown,
  faChevronRight,
  faCircleInfo
} from '@fortawesome/free-solid-svg-icons';

interface SidebarProps {
  isOpen: boolean;
}

interface MenuItem {
  path?: string;
  icon: any;
  label: string;
  children?: MenuItem[];
}

export default function Sidebar({ isOpen }: SidebarProps) {
  const location = useLocation();
  const [expandedMenus, setExpandedMenus] = useState<string[]>(['Análisis', 'Gramática']);

  const menuItems: MenuItem[] = [
    { path: '/', icon: faChartPie, label: 'Dashboard' },
    {
      icon: faChartLine,
      label: 'Análisis',
      children: [
        { path: '/analysis/text', icon: faPenToSquare, label: 'Análisis de Texto' },
        { path: '/analysis/document', icon: faFileUpload, label: 'Análisis de Documentos' },
        { path: '/analysis/web', icon: faGlobe, label: 'Análisis Web' },
      ]
    },
    {
      icon: faSpellCheck,
      label: 'Gramática',
      children: [
        { path: '/corpus', icon: faLanguage, label: 'Corpus Léxico' },
        { path: '/dictionaries', icon: faBook, label: 'Glosarios' },
      ]
    },
    { path: '/help', icon: faCircleInfo, label: 'Ayuda' },
    { path: '/settings', icon: faGear, label: 'Configuración' },
  ];

  const toggleMenu = (label: string) => {
    setExpandedMenus(prev =>
      prev.includes(label)
        ? prev.filter(item => item !== label)
        : [...prev, label]
    );
  };

  const isMenuActive = (children?: MenuItem[]) => {
    if (!children) return false;
    return children.some(child => child.path === location.pathname);
  };

  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedMenus.includes(item.label);
    const isActive = isMenuActive(item.children);

    if (hasChildren) {
      return (
        <div key={item.label}>
          <button
            onClick={() => toggleMenu(item.label)}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
              isActive
                ? 'bg-blue-50 text-blue-700'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <div className="flex items-center gap-3">
              <FontAwesomeIcon icon={item.icon} className="text-lg" />
              <span className="font-medium">{item.label}</span>
            </div>
            <FontAwesomeIcon
              icon={isExpanded ? faChevronDown : faChevronRight}
              className="text-sm text-gray-400"
            />
          </button>

          {isExpanded && (
            <div className="ml-4 mt-1 space-y-1">
              {item.children?.map(child => renderMenuItem(child, level + 1))}
            </div>
          )}
        </div>
      );
    }

    return (
      <NavLink
        key={item.path}
        to={item.path!}
        end={item.path === '/'}
        className={({ isActive }) =>
          `flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
            level > 0 ? 'ml-4' : ''
          } ${
            isActive
              ? 'bg-blue-600 text-white shadow-md'
              : 'text-gray-700 hover:bg-gray-100'
          }`
        }
      >
        <FontAwesomeIcon icon={item.icon} className="text-lg" />
        <span className="font-medium">{item.label}</span>
      </NavLink>
    );
  };

  return (
    <aside
      className={`fixed left-0 top-[73px] h-[calc(100vh-73px)] bg-white border-r border-gray-200 transition-all duration-300 ${
        isOpen ? 'w-64 translate-x-0' : 'w-64 -translate-x-full'
      }`}
    >
      <nav className="p-4 space-y-2">
        {menuItems.map(item => renderMenuItem(item))}
      </nav>
    </aside>
  );
}
